import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "Services", href: "/services" },
  { label: "About", href: "/about" },
  { label: "Service Areas", href: "/location-dallas" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 bg-white transition-all duration-500 ${
        scrolled
          ? "py-2.5 shadow-[0_2px_16px_rgba(0,0,0,0.07)] border-b border-gray-100/80"
          : "py-4 border-b border-gray-100"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between gap-8">

        {/* Logo */}
        <Link to="/" className="flex items-center flex-shrink-0 group" aria-label="KISA Cooling Dallas — Home">
          <img
            src="https://img1.wsimg.com/isteam/ip/ef6b4008-7081-4736-a8f7-eb15cb13938a/1000014942.jpg/:/rs=h:200,cg:true,m/qt=q:95"
            alt="Knight In Shining Armor Heating & Air — KISA Cooling Dallas"
            className={`w-auto object-contain transition-all duration-500 hidden md:block ${scrolled ? "h-[46px]" : "h-[55px]"}`}
            style={{ imageRendering: "crisp-edges" }}
          />
          <img
            src="https://img1.wsimg.com/isteam/ip/ef6b4008-7081-4736-a8f7-eb15cb13938a/1000014942.jpg/:/rs=h:200,cg:true,m/qt=q:95"
            alt="KISA Cooling Dallas"
            className="h-[42px] w-auto object-contain md:hidden"
            style={{ imageRendering: "crisp-edges" }}
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-9" aria-label="Main navigation">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`relative font-['Inter'] text-[13.5px] font-[500] tracking-[0.01em] transition-colors duration-300 whitespace-nowrap group ${
                location.pathname === link.href
                  ? "text-[#1e3a5f]"
                  : "text-gray-500 hover:text-gray-900"
              }`}
            >
              {link.label}
              <span
                className={`absolute -bottom-0.5 left-0 h-[1.5px] bg-[#1e3a5f] transition-all duration-300 ${
                  location.pathname === link.href ? "w-full" : "w-0 group-hover:w-full"
                }`}
              />
            </Link>
          ))}
        </nav>

        {/* Desktop CTA Group */}
        <div className="hidden md:flex items-center gap-5">
          <a
            href="tel:2148703185"
            className="flex items-center gap-2 text-gray-600 hover:text-[#1e3a5f] font-['Inter'] text-[13.5px] font-[500] transition-colors duration-300 whitespace-nowrap group"
            aria-label="Call KISA Cooling Dallas"
          >
            <div className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 group-hover:border-[#1e3a5f]/30 transition-colors duration-300">
              <i className="ri-phone-line text-[#1e3a5f] text-[12px]"></i>
            </div>
            <span>214-870-3185</span>
          </a>

          <a
            href="#quote"
            className="bg-[#1e3a5f] hover:bg-[#162d4a] active:bg-[#0f2038] text-white font-['Inter'] text-[13.5px] font-[600] px-6 py-2.5 rounded-md transition-all duration-300 whitespace-nowrap tracking-[0.01em] hover:shadow-[0_4px_16px_rgba(30,58,95,0.3)] hover:-translate-y-[1px]"
          >
            Request Service
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden w-9 h-9 flex items-center justify-center text-gray-700 rounded-lg hover:bg-gray-100 transition-colors duration-200 cursor-pointer flex-shrink-0"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle navigation menu"
          aria-expanded={menuOpen}
        >
          <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-3-line"}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          menuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="bg-white border-t border-gray-100 px-6 py-6 flex flex-col gap-5">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`font-['Inter'] text-base font-[500] transition-colors duration-200 ${
                location.pathname === link.href ? "text-[#1e3a5f]" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="border-t border-gray-100 pt-4 flex flex-col gap-3">
            <a
              href="tel:2148703185"
              className="flex items-center gap-2.5 font-['Inter'] text-[#1e3a5f] text-base font-[600]"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line text-sm"></i>
              </div>
              214-870-3185
            </a>
            <a
              href="#quote"
              className="bg-[#1e3a5f] text-white font-['Inter'] text-sm font-[600] px-5 py-3 rounded-md text-center whitespace-nowrap tracking-[0.01em]"
            >
              Request Service
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
